/**
 * CountryAnalytics Component
 * Admin dashboard for country-based analytics
 * 
 * Features:
 * - Registrations by country
 * - Rewarded ads by country
 * - Purchases by country
 * - Turkey and Azerbaijan highlighted
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Country flags mapping
const COUNTRY_FLAGS = {
  'TR': '🇹🇷',
  'AZ': '🇦🇿',
  'US': '🇺🇸',
  'DE': '🇩🇪',
  'GB': '🇬🇧',
  'FR': '🇫🇷',
  'NL': '🇳🇱',
  'BE': '🇧🇪',
  'AT': '🇦🇹',
  'CH': '🇨🇭',
  'unknown': '🌍'
};

const getFlag = (code) => COUNTRY_FLAGS[code] || '🌍';

const CountryAnalytics = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // overview, registrations, ads, purchases

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/');
        return;
      }

      const { data } = await axios.get(`${API_URL}/api/country-analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAnalytics(data);
      setLoading(false);
    } catch (err) {
      console.error('Analytics fetch error:', err);
      if (err.response?.status === 403) {
        setError('Admin yetkisi gerekli!');
      } else {
        setError(err.response?.data?.detail || 'Veriler yüklenemedi');
      }
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center', 
        background: 'linear-gradient(135deg, #0d0d2b 0%, #1a0533 100%)', 
        minHeight: '100vh', 
        color: 'white' 
      }}>
        <h2>🌍 Yükleniyor...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center', 
        background: 'linear-gradient(135deg, #0d0d2b 0%, #1a0533 100%)', 
        minHeight: '100vh', 
        color: 'white' 
      }}>
        <h2>❌ Hata</h2>
        <p>{error}</p>
        <button 
          onClick={() => navigate('/admin')}
          style={{
            background: '#8b5cf6',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            cursor: 'pointer',
            marginTop: '20px'
          }}
        >
          ← Admin Panele Dön
        </button>
      </div>
    );
  }

  const { totals, highlighted_countries, registrations, rewarded_ads, purchases } = analytics || {};

  const renderHighlightedCountries = () => (
    <div style={{ marginBottom: '32px' }}>
      <h2 style={{ fontSize: '1.3rem', margin: '0 0 20px', color: 'rgba(255,255,255,0.9)' }}>
        🎯 Öncelikli Pazarlar
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
        {/* Turkey */}
        <div style={{ 
          background: 'linear-gradient(135deg, rgba(239,68,68,0.15), rgba(239,68,68,0.05))',
          border: '2px solid rgba(239,68,68,0.4)',
          borderRadius: '20px', 
          padding: '24px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
            <span style={{ fontSize: '48px' }}>🇹🇷</span>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.4rem', color: 'white' }}>Türkiye</h3>
              <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>TR</p>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            <div style={{ textAlign: 'center', background: 'rgba(255,255,255,0.05)', borderRadius: '12px', padding: '12px' }}>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>Kayıtlar</div>
              <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#ef4444' }}>
                {highlighted_countries?.turkey?.registrations?.count || 0}
              </div>
            </div>
            <div style={{ textAlign: 'center', background: 'rgba(255,255,255,0.05)', borderRadius: '12px', padding: '12px' }}>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>Reklamlar</div>
              <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#4ade80' }}>
                {highlighted_countries?.turkey?.rewarded_ads?.count || 0}
              </div>
            </div>
            <div style={{ textAlign: 'center', background: 'rgba(255,255,255,0.05)', borderRadius: '12px', padding: '12px' }}>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>Satışlar</div>
              <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#c084fc' }}>
                {highlighted_countries?.turkey?.purchases?.count || 0}
              </div>
            </div>
          </div>
        </div>

        {/* Azerbaijan */}
        <div style={{ 
          background: 'linear-gradient(135deg, rgba(59,130,246,0.15), rgba(59,130,246,0.05))',
          border: '2px solid rgba(59,130,246,0.4)',
          borderRadius: '20px', 
          padding: '24px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
            <span style={{ fontSize: '48px' }}>🇦🇿</span>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.4rem', color: 'white' }}>Azerbaycan</h3>
              <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>AZ</p>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            <div style={{ textAlign: 'center', background: 'rgba(255,255,255,0.05)', borderRadius: '12px', padding: '12px' }}>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>Kayıtlar</div>
              <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#3b82f6' }}>
                {highlighted_countries?.azerbaijan?.registrations?.count || 0}
              </div>
            </div>
            <div style={{ textAlign: 'center', background: 'rgba(255,255,255,0.05)', borderRadius: '12px', padding: '12px' }}>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>Reklamlar</div>
              <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#4ade80' }}>
                {highlighted_countries?.azerbaijan?.rewarded_ads?.count || 0}
              </div>
            </div>
            <div style={{ textAlign: 'center', background: 'rgba(255,255,255,0.05)', borderRadius: '12px', padding: '12px' }}>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>Satışlar</div>
              <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#c084fc' }}>
                {highlighted_countries?.azerbaijan?.purchases?.count || 0}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCountryTable = (data, title, showRevenue = false) => (
    <div style={{ 
      background: 'rgba(255,255,255,0.05)', 
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '16px', 
      padding: '24px',
      marginBottom: '24px'
    }}>
      <h3 style={{ fontSize: '1.1rem', margin: '0 0 16px', color: 'rgba(255,255,255,0.9)' }}>
        {title}
      </h3>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <th style={{ padding: '12px', textAlign: 'left', color: 'rgba(255,255,255,0.6)', fontWeight: '500' }}>Ülke</th>
              <th style={{ padding: '12px', textAlign: 'right', color: 'rgba(255,255,255,0.6)', fontWeight: '500' }}>Sayı</th>
              {showRevenue && (
                <th style={{ padding: '12px', textAlign: 'right', color: 'rgba(255,255,255,0.6)', fontWeight: '500' }}>Gelir</th>
              )}
            </tr>
          </thead>
          <tbody>
            {data && data.length > 0 ? (
              data.slice(0, 15).map((item, i) => (
                <tr 
                  key={i} 
                  style={{ 
                    borderBottom: '1px solid rgba(255,255,255,0.05)',
                    background: item.is_priority ? 'rgba(139,92,246,0.1)' : 'transparent'
                  }}
                >
                  <td style={{ padding: '12px', color: 'white' }}>
                    <span style={{ marginRight: '8px' }}>{getFlag(item.country_code)}</span>
                    {item.country_name}
                    {item.is_priority && (
                      <span style={{ 
                        marginLeft: '8px', 
                        fontSize: '10px', 
                        background: 'rgba(139,92,246,0.3)', 
                        padding: '2px 6px', 
                        borderRadius: '4px',
                        color: '#c084fc'
                      }}>
                        Öncelikli
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', color: '#4ade80', fontWeight: '600' }}>
                    {item.count?.toLocaleString() || 0}
                  </td>
                  {showRevenue && (
                    <td style={{ padding: '12px', textAlign: 'right', color: '#fbbf24' }}>
                      {item.revenue ? `₺${item.revenue.toLocaleString()}` : '-'}
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={showRevenue ? 3 : 2} style={{ padding: '24px', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
                  Henüz veri yok
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div style={{ 
      background: 'linear-gradient(135deg, #0d0d2b 0%, #1a0533 100%)', 
      minHeight: '100vh', 
      padding: '40px',
      color: 'white'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <div>
            <h1 style={{ fontSize: '2rem', margin: '0 0 8px' }}>🌍 Ülke Analytics</h1>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)' }}>
              Kayıtlar, Reklamlar ve Satışlar - Ülke Bazlı
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={fetchAnalytics}
              style={{
                background: 'rgba(74,222,128,0.2)',
                color: '#4ade80',
                border: '1px solid rgba(74,222,128,0.3)',
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              🔄 Yenile
            </button>
            <button 
              onClick={() => navigate('/admin')}
              style={{
                background: 'rgba(255,255,255,0.1)',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              ← Admin Panel
            </button>
          </div>
        </div>

        {/* Global Totals */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', 
          gap: '16px', 
          marginBottom: '32px' 
        }}>
          <div style={{ 
            background: 'rgba(255,255,255,0.05)', 
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '16px', 
            padding: '20px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '28px', marginBottom: '4px' }}>👥</div>
            <div style={{ fontSize: '1.8rem', fontWeight: '700', color: '#60a5fa' }}>
              {totals?.registrations?.toLocaleString() || 0}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>Toplam Kayıt</div>
          </div>

          <div style={{ 
            background: 'rgba(255,255,255,0.05)', 
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '16px', 
            padding: '20px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '28px', marginBottom: '4px' }}>📺</div>
            <div style={{ fontSize: '1.8rem', fontWeight: '700', color: '#4ade80' }}>
              {totals?.rewarded_ads?.toLocaleString() || 0}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>Toplam Reklam</div>
          </div>

          <div style={{ 
            background: 'rgba(255,255,255,0.05)', 
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '16px', 
            padding: '20px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '28px', marginBottom: '4px' }}>💎</div>
            <div style={{ fontSize: '1.8rem', fontWeight: '700', color: '#c084fc' }}>
              {totals?.credits_from_ads?.toLocaleString() || 0}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>Reklam Kredisi</div>
          </div>

          <div style={{ 
            background: 'rgba(255,255,255,0.05)', 
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '16px', 
            padding: '20px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '28px', marginBottom: '4px' }}>💳</div>
            <div style={{ fontSize: '1.8rem', fontWeight: '700', color: '#fbbf24' }}>
              {totals?.purchases?.toLocaleString() || 0}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>Toplam Satış</div>
          </div>

          <div style={{ 
            background: 'rgba(255,255,255,0.05)', 
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '16px', 
            padding: '20px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '28px', marginBottom: '4px' }}>💰</div>
            <div style={{ fontSize: '1.8rem', fontWeight: '700', color: '#f472b6' }}>
              ₺{totals?.revenue?.toLocaleString() || 0}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>Toplam Gelir</div>
          </div>
        </div>

        {/* Highlighted Countries */}
        {renderHighlightedCountries()}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
          {[
            { id: 'registrations', label: '👥 Kayıtlar', icon: '👥' },
            { id: 'ads', label: '📺 Reklamlar', icon: '📺' },
            { id: 'purchases', label: '💳 Satışlar', icon: '💳' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '12px 24px',
                background: activeTab === tab.id ? 'rgba(139,92,246,0.3)' : 'rgba(255,255,255,0.05)',
                border: `1px solid ${activeTab === tab.id ? 'rgba(139,92,246,0.5)' : 'rgba(255,255,255,0.1)'}`,
                borderRadius: '10px',
                color: activeTab === tab.id ? '#c084fc' : 'rgba(255,255,255,0.7)',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeTab === tab.id ? '600' : '400',
                transition: 'all 0.2s'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'registrations' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}>
            {renderCountryTable(registrations?.total, '📊 Tüm Zamanlar - Kayıtlar')}
            {renderCountryTable(registrations?.week, '📅 Son 7 Gün - Kayıtlar')}
            {renderCountryTable(registrations?.today, '🕐 Bugün - Kayıtlar')}
          </div>
        )}

        {activeTab === 'ads' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}>
            {renderCountryTable(rewarded_ads?.total, '📊 Tüm Zamanlar - Reklamlar', false)}
            {renderCountryTable(rewarded_ads?.week, '📅 Son 7 Gün - Reklamlar', false)}
            {renderCountryTable(rewarded_ads?.today, '🕐 Bugün - Reklamlar', false)}
          </div>
        )}

        {activeTab === 'purchases' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}>
            {renderCountryTable(purchases?.total, '📊 Tüm Zamanlar - Satışlar', true)}
            {renderCountryTable(purchases?.week, '📅 Son 7 Gün - Satışlar', true)}
            {renderCountryTable(purchases?.today, '🕐 Bugün - Satışlar', true)}
          </div>
        )}

        {/* Last Updated */}
        <div style={{ 
          marginTop: '32px', 
          textAlign: 'center', 
          color: 'rgba(255,255,255,0.4)',
          fontSize: '13px'
        }}>
          Son güncelleme: {analytics?.last_updated ? new Date(analytics.last_updated).toLocaleString('tr-TR') : '-'}
        </div>
      </div>
    </div>
  );
};

export default CountryAnalytics;
